import React, { useEffect, useRef, useState } from 'react';
import { Play, Volume2, VolumeX, Youtube } from 'lucide-react';
import { trackYouTubeChannelClick } from '@/lib/metaPixel';
import { trackGrowth } from '@/lib/growthTracking';

const MILESTONES = [25, 50, 75];

/**
 * Hero video embed with autoplay-muted, native YouTube controls for
 * manual unmute, and a custom replay overlay after the video ends.
 *
 * Design choices:
 *   • `mute=1 + autoplay=1 + playsinline=1`  — the only combination
 *     iOS Safari respects for inline autoplay (hero slot must play on
 *     first paint without a user gesture).
 *   • `modestbranding=1 + rel=0`             — minimal YouTube chrome;
 *     the "related videos" overlay at the end is scoped to the same
 *     channel so viewers stay within the Schedule Guru brand.
 *   • IFrame Player API                       — the only reliable way
 *     to detect video-end so we can swap in our own replay card.
 *   • Loads the API script ONCE, globally     — React 18 StrictMode
 *     double-mounts dev components, so the global guard prevents the
 *     "YT is already defined" console noise.
 */

const PLAYER_API_SRC = 'https://www.youtube.com/iframe_api';
let _apiLoadingPromise = null;
function loadYouTubeApi() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_apiLoadingPromise) return _apiLoadingPromise;
  _apiLoadingPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = PLAYER_API_SRC;
    tag.async = true;
    document.head.appendChild(tag);
  });
  return _apiLoadingPromise;
}

export function HeroVideo({
  videoId,
  title = 'Schedule Guru intro',
  channelUrl = 'https://www.youtube.com/@ScheduleGuruLeagueSoftware?sub_confirmation=1',
  channelLabel = 'Subscribe to our channel',
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!videoId || !containerRef.current) return;
    let cancelled = false;
    let timer = null;
    const fired = new Set();
    const fire = (m) => {
      if (fired.has(m)) return;
      fired.add(m);
      trackGrowth('video_progress', { milestone: m });
    };
    const stopPoll = () => { clearInterval(timer); timer = null; };
    const startPoll = () => {
      if (timer) return;
      timer = setInterval(() => {
        try {
          const p = playerRef.current;
          const pct = (p.getCurrentTime() / p.getDuration()) * 100;
          MILESTONES.forEach((m) => pct >= m && fire(m));
        } catch (_e) { /* player not ready */ }
      }, 1000);
    };
    loadYouTubeApi().then((YT) => {
      if (cancelled || !YT || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          controls: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: (e) => {
            // YT.PlayerState.ENDED = 0
            if (e.data === 0) { setEnded(true); fire(100); }
            // PLAYING = 1 — hide the overlay if a user hits YouTube's
            // native play button on the end-card instead of ours.
            if (e.data === 1) { setEnded(false); startPoll(); } else stopPoll();
          },
        },
      });
    });
    return () => {
      cancelled = true;
      stopPoll();
      try { playerRef.current && playerRef.current.destroy(); } catch (_e) {}
      playerRef.current = null;
    };
  }, [videoId]);

  const handleReplay = () => {
    if (!playerRef.current) return;
    try {
      playerRef.current.seekTo(0, true);
      playerRef.current.playVideo();
      setEnded(false);
    } catch (_e) { /* no-op */ }
  };

  const handleToggleMute = () => {
    if (!playerRef.current) return;
    try {
      if (muted) {
        playerRef.current.unMute();
        setMuted(false);
      } else {
        playerRef.current.mute();
        setMuted(true);
      }
    } catch (_e) { /* no-op */ }
  };

  return (
    <div className="w-full">
      <div
        className="relative w-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/15 bg-black/40"
        style={{ aspectRatio: '16 / 9' }}
        data-testid="hero-video"
      >
      {/* Decorative gold glow behind the frame */}
      <div
        aria-hidden
        className="absolute -inset-6 rounded-3xl bg-amber-400/15 blur-2xl -z-10"
      />

      {/* YouTube IFrame mounts into this div. The API replaces its
          contents with an <iframe>, so the ref itself stays stable. */}
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />

      {/* Floating UNMUTE pill — fades out after the user hits unmute. */}
      {!ended && (
        <button
          type="button"
          onClick={handleToggleMute}
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur hover:bg-black/80 text-white text-xs font-semibold transition"
          data-testid="hero-video-mute-toggle"
          aria-label={muted ? 'Unmute video' : 'Mute video'}
        >
          {muted ? (
            <><VolumeX className="w-3.5 h-3.5" /> Tap to unmute</>
          ) : (
            <><Volume2 className="w-3.5 h-3.5" /> Mute</>
          )}
        </button>
      )}

      {/* Replay overlay — swaps in when YouTube reports state=ENDED. */}
      {ended && (
        <button
          type="button"
          onClick={handleReplay}
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#00247D]/90 via-[#001a5e]/90 to-black/95 text-white transition-opacity animate-fade-in-up"
          data-testid="hero-video-replay-btn"
          aria-label="Replay video"
        >
          <div className="w-20 h-20 rounded-full bg-amber-400 text-[#00247D] flex items-center justify-center shadow-xl hover:scale-105 transition-transform">
            <Play className="w-8 h-8 ml-1" fill="currentColor" />
          </div>
          <div className="text-sm font-bold uppercase tracking-widest text-amber-200">Replay</div>
        </button>
      )}
      </div>

      {/* Thin "Subscribe to our channel" chip — sits just below the
          player, matching the hero's dark navy palette with YouTube red
          as the accent so it reads as a channel CTA without screaming. */}
      {channelUrl && (
        <a
          href={channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { trackYouTubeChannelClick('landing_hero'); trackGrowth('channel_click'); }}
          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 hover:border-white/25 text-xs font-medium text-slate-200 transition"
          data-testid="hero-video-subscribe-chip"
          aria-label={channelLabel}
        >
          <Youtube className="w-4 h-4 text-[#FF0000]" />
          <span>{channelLabel}</span>
          <span className="text-slate-400">→</span>
        </a>
      )}
    </div>
  );
}

export default HeroVideo;
